import { API } from 'app/routes';
import axios from 'axios';
import { Action, action, computed, Computed, Thunk, thunk } from 'easy-peasy';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { jsonToFormData } from '~/app/helpers/headerContentType';
import { StoreModel } from '~/store';
import { CreateWorkOrderPayload, CreateWorkOrderResponse, DeleteIssueServiceAttachmentPayload, DeleteWorkOrderAttachmentPayload, FilterWorkOrderPayload, RemoveWorkOrderTaskPayload, RemoveWorkOrderTaskResponse, SaveAdministrativeDataPayload, SaveAdministrativeDataResponse, SaveIssueServiceAttachmentPayload, SaveWorkOrderAttachmentPayload, SaveWorkOrderPayload, SaveWorkOrderResponse, SaveWorkOrderTasksPayload, SaveWorkOrderTasksResponse, UpdateWorkOrderStatusPayload, UpdateWorkOrderStatusResponse, WorkOrderAttachmentResponse, WorkOrderAutoSaveDataPayload, WorkOrderAutoSaveDataResponse, WorkOrderAutoSaveFieldKey, WorkOrderDepartment, WorkOrderDetail, WorkOrderDetailResponse, WorkOrderExcavatorTaskListPayload, WorkOrderExcavatorTaskListResponse, WorkOrderFilterOptions, WorkOrderGeneralSettings, WorkOrderHistoryPayload, WorkOrderHistoryPayloadType, WorkOrderHistoryResponse, WorkOrderMechanic, WorkOrderMechanicPayload, WorkOrderMechanicResponse, WorkOrderStatusesList, WorkOrderTaskDetail, WorkOrderTaskDetailPayload, WorkOrderTaskDetailResponse, WorkOrderTaskStatus, WorkOrderTimeEntryPayload, WorkOrderTimeEntryResponse, WorkOrderPartList } from '~/workorder/types';
import { Part, SavePartResponse, SavePartPayload, SearchPartResponse, Material, MiscMaterial, SaveMaterialPayload, SaveMaterialResponse } from '~/types';
import { applyWorkOrderDataByKey } from '~/workorder/helper';

export interface WorkOrderModel {
    fetching: boolean
    setFetching: Action<WorkOrderModel, boolean>
    data: WorkOrderDetail
    setData: Action<WorkOrderModel, WorkOrderDetail>
    taskData: WorkOrderTaskDetail
    setTaskData: Action<WorkOrderModel, WorkOrderTaskDetail>
    getWorkOrderDetail: Thunk<WorkOrderModel, { id: number; showLoading?: boolean }, any, StoreModel, Promise<void>>
    getWorkOrderTaskDetail: Thunk<WorkOrderModel, WorkOrderTaskDetailPayload, any, StoreModel, Promise<void>>
    getWorkOrderHistory: Thunk<WorkOrderModel, WorkOrderHistoryPayload, any, StoreModel, Promise<WorkOrderHistoryResponse>>
    filters: FilterWorkOrderPayload
    setFilters: Action<WorkOrderModel, FilterWorkOrderPayload>
    machineFilters: FilterWorkOrderPayload
    setMachineFilters: Action<WorkOrderModel, FilterWorkOrderPayload>
    filterOptions: WorkOrderFilterOptions
    setFilterOptions: Action<WorkOrderModel, WorkOrderFilterOptions>
    taskCount: Computed<WorkOrderModel, {total: number; done: number;}>
    fetchExcavatorTaskList: Thunk<WorkOrderModel, WorkOrderExcavatorTaskListPayload, any, StoreModel, Promise<WorkOrderExcavatorTaskListResponse>>
    saveWorkOrderTasks: Thunk<WorkOrderModel, SaveWorkOrderTasksPayload, any, StoreModel, Promise<SaveWorkOrderTasksResponse>>
    removeWorkOrderTask: Thunk<WorkOrderModel, RemoveWorkOrderTaskPayload, any, StoreModel, Promise<RemoveWorkOrderTaskResponse>>
    workOrderGeneralSettings: WorkOrderGeneralSettings
    setWorkOrderGeneralSettings: Action<WorkOrderModel, WorkOrderGeneralSettings>
    createWorkOrder: Thunk<WorkOrderModel, CreateWorkOrderPayload, any, StoreModel, Promise<CreateWorkOrderResponse>>
    departments: WorkOrderDepartment[]
    setDepartments: Action<WorkOrderModel, WorkOrderDepartment[]>
    fetchingDepartments: boolean
    setFetchingDepartments: Action<WorkOrderModel, boolean>
    getWorkorderDepartments: Thunk<WorkOrderModel, void, any, StoreModel, Promise<void>>
    updateWorkOrderStatus: Thunk<WorkOrderModel, UpdateWorkOrderStatusPayload, any, StoreModel, Promise<UpdateWorkOrderStatusResponse>>
    saveWorkOrder: Thunk<WorkOrderModel, SaveWorkOrderPayload, any, StoreModel, Promise<SaveWorkOrderResponse>>
    addMechanic: Action<WorkOrderModel, WorkOrderMechanic>
    removeMechanic: Action<WorkOrderModel, number>
    updateWorkOrderMechanic: Thunk<WorkOrderModel, WorkOrderMechanicPayload, any, StoreModel, Promise<WorkOrderMechanicResponse>>
    updateWorkOrderDataByKey: Action<WorkOrderModel, { key: WorkOrderAutoSaveFieldKey; value: string }>
    autoSaveWorkOrderData: Thunk<WorkOrderModel, WorkOrderAutoSaveDataPayload, any, StoreModel, Promise<WorkOrderAutoSaveDataResponse>>
    saveAdministrativeData: Thunk<WorkOrderModel, SaveAdministrativeDataPayload, any, StoreModel, Promise<SaveAdministrativeDataResponse>>
    timeEntrySaving: boolean
    setTimeEntrySaving: Action<WorkOrderModel, boolean>
    saveWorkOrderTimeEntry: Thunk<WorkOrderModel, WorkOrderTimeEntryPayload, any, StoreModel, Promise<WorkOrderTimeEntryResponse>>
    updateWorkOrderTimeEntry: Thunk<WorkOrderModel, WorkOrderTimeEntryPayload, any, StoreModel, Promise<WorkOrderTimeEntryResponse>>
    deleteWorkOrderTimeEntry: Thunk<WorkOrderModel, number, any, StoreModel, Promise<WorkOrderTimeEntryResponse>>
    attachmentSaving: boolean
    setAttachmentSaving: Action<WorkOrderModel, boolean>
    saveWorkOrderAttachment: Thunk<WorkOrderModel, SaveWorkOrderAttachmentPayload, any, StoreModel, Promise<WorkOrderAttachmentResponse>>
    saveIssueServiceAttachment: Thunk<WorkOrderModel, SaveIssueServiceAttachmentPayload, any, StoreModel, Promise<WorkOrderAttachmentResponse>>
    deleteIssueServiceAttachment: Thunk<WorkOrderModel, DeleteIssueServiceAttachmentPayload, any, StoreModel, Promise<WorkOrderAttachmentResponse>>
    deleteWorkOrderAttachment: Thunk<WorkOrderModel, DeleteWorkOrderAttachmentPayload, any, StoreModel, Promise<WorkOrderAttachmentResponse>>
    partUpdating: boolean
    setPartUpdating: Action<WorkOrderModel, boolean>
    tempParts: Part[]
    setTempParts: Action<WorkOrderModel, Part[]>;
    setParts: Action<WorkOrderModel, WorkOrderPartList[]>;
    fetchParts: Thunk<WorkOrderModel, string, any, StoreModel, Promise<void>>
    searchParts: Part[]
    searchPartsTotal: number
    setSearchedParts: Action<WorkOrderModel, SearchPartResponse>
    fetchSearchParts: Thunk<WorkOrderModel, { searchText: string, offset: number }, any, StoreModel, Promise<void>>
    savePart: Thunk<WorkOrderModel, SavePartPayload, any, StoreModel, Promise<SavePartResponse>>
    materialUpdating: boolean
    setMaterialUpdating: Action<WorkOrderModel, boolean>
    tempMaterials: Material[]
    setTempMaterials: Action<WorkOrderModel, Material[]>;
    setMaterials: Action<WorkOrderModel, Material[]>;
    fetchMaterials: Thunk<WorkOrderModel, string, any, StoreModel, Promise<void>>
    miscMaterials: MiscMaterial[]
    setMiscMaterials: Action<WorkOrderModel, MiscMaterial[]>
    fetchMiscMaterials: Thunk<WorkOrderModel, number, any, StoreModel, Promise<void>>
    saveMaterials: Thunk<WorkOrderModel, SaveMaterialPayload, any, StoreModel, Promise<SaveMaterialResponse>>
}

export const workOrderModel: WorkOrderModel = {
    fetching: false,
    setFetching: action((state, payload) => {
        state.fetching = payload;
    }),
    data: {
        id: 0,
        history: [],
        workOrderNo: '',
        status: 0,
        statusInfo: {
            comment: null,
            statusAt: null,
            statusBy: null
        },
        isStarted: 0,
        lastUpdated: {
            date: null,
            name: null
        },
        excavator: {
            id: 0,
            name: '',
            intern: '',
            isOperatingTimeVisible: 0,
            operatingTimeAllowed: 0,
            isOperatingCountVisible: 0,
            operatingCountAllowed: 0,
            tracker: null,
        },
        operatingTime: null,
        operatingCount: null,
        createdBy: {
            id: 0,
            name: ''
        },
        approvedBy: {
            id: 0,
            name: ''
        },
        personnel: {
            id: 0,
            name: ''
        },
        mechanics: [],
        workType: 0,
        repairLocation: '',
        costCategoryId: null,
        departmentId: null,
        departmentPrefix: null,
        chargedDepartmentId: null,
        customerId: null,
        projectId: null,
        sortingType: null,
        invoiceAcceptanceUser: null,
        invoiceAcceptanceUserId: null,
        scheduledStartDate: '',
        scheduledEndDate: '',
        actualStartDate: null,
        actualEndDate: null,
        description: '',
        comment: '',
        attachments: [],
        tasks: [],
        summary: {
            totalHours: 0,
            totalParts: 0,
            totalMaterials: 0
        },
        parts: [],
        materials: []
    },
    setData: action((state, payload) => {
        state.data = {
            ...payload,
            parts: state.data.parts,
            materials: state.data.materials
        };
    }),
    getWorkOrderDetail: thunk((actions, payload) => {
        if (payload.showLoading) {
            actions.setFetching(true);
        }
        return axios.get<null, WorkOrderDetailResponse>(`${API.workOrder}/${payload.id}`)
            .then(data => {
                actions.setData(data.data);
            })
            .finally(() => {
                if (payload.showLoading) {
                    actions.setFetching(false);
                }
            });
    }),
    getWorkOrderTaskDetail: thunk((actions, payload) => {
        const showLoading = payload.showLoading !== false;
        if (showLoading) {
            actions.setFetching(true);
        }
        return axios.post<null, WorkOrderTaskDetailResponse>(`${API.workOrder}/${payload.id}/tasks/${payload.taskId}`, { type: payload.type })
            .then(data => {
                actions.setTaskData(data.data);
            })
            .finally(() => {
                if (showLoading) {
                    actions.setFetching(false);
                }
            });
    }),
    getWorkOrderHistory: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, WorkOrderHistoryResponse>(`${API.workOrder}/history`, payload)
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    filters: {
        searchText: "",
        type: WorkOrderHistoryPayloadType.ALL,
        departments: [],
        status: []
    },
    setFilters: action((state, payload) => {
        state.filters = payload;
    }),
    machineFilters: {
        searchText: "",
        type: WorkOrderHistoryPayloadType.ALL,
        departments: [],
        status: []
    },
    setMachineFilters: action((state, payload) => {
        state.machineFilters = payload;
    }),
    filterOptions: {
        status: WorkOrderStatusesList.map(status => ({ id: status.toString(), name: status.toString() }))
    },
    setFilterOptions: action((state, payload) => {
        state.filterOptions = payload;
    }),
    taskData: {
        id: 0,
        title: '',
        type: IssueOrServiceType.ISSUE,
        status: 0,
        description: '',
        users: [],
        parts: [],
        materials: [],
        attachments: [],
        checklist: { completed: 0, total: 0 }
    },
    setTaskData: action((state, payload) => {
        state.taskData = payload;
    }),
    taskCount: computed((state) => {
        const tasks = state.data.tasks ?? [];
        const totalCount = tasks.length;
        const doneCount = tasks.filter(task => task.status === WorkOrderTaskStatus.COMPLETED || task.status === WorkOrderTaskStatus.REMOVED).length;
        return { total: totalCount, done: doneCount };
    }),
    fetchExcavatorTaskList: thunk((_actions, payload) => {
        const { machineId, searchText, offset } = payload;
        return axios.post<null, WorkOrderExcavatorTaskListResponse>(
            `${API.workOrder}/excavator-task-list/${machineId}`,
            { searchText, offset },
        );
    }),
    saveWorkOrderTasks: thunk((actions, payload) => {
        actions.setFetching(true);
        const { workOrderId, tasks } = payload;
        return axios.post<null, SaveWorkOrderTasksResponse>(`${API.workOrder}/${workOrderId}/tasks/save`, { tasks })
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    removeWorkOrderTask: thunk((actions, payload) => {
        actions.setFetching(true);
        const { workOrderId, taskId, deleteParts, type } = payload;
        return axios.post<null, RemoveWorkOrderTaskResponse>(`${API.workOrder}/${workOrderId}/tasks/remove`, {
            taskId,
            deleteParts,
            type,
        })
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    workOrderGeneralSettings: {
        hoursCount: 0,
        plannedStartDate: '',
        plannedEndDate: '',
        setLiveOperatingHours: false,
        allowManualOperatingTimeUpdate: false,
    },
    setWorkOrderGeneralSettings: action((state, payload) => {
        state.workOrderGeneralSettings = payload ?? {};
    }),
    createWorkOrder: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, CreateWorkOrderResponse>(`${API.workOrder}/create`, payload)
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    departments: [],
    setDepartments: action((state, payload) => {
        state.departments = payload;
    }),
    fetchingDepartments: false,
    setFetchingDepartments: action((state, payload) => {
        state.fetchingDepartments = payload;
    }),
    getWorkorderDepartments: thunk((actions) => {
        actions.setFetchingDepartments(true);
        return axios.get<null, { data: WorkOrderDepartment[] }>(`${API.workOrder}/departments`)
            .then(data => {
                actions.setDepartments(data.data);
            })
            .finally(() => actions.setFetchingDepartments(false));
    }),
    updateWorkOrderStatus: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, UpdateWorkOrderStatusResponse>(`${API.workOrder}/update-status`, payload)
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    saveWorkOrder: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, SaveWorkOrderResponse>(`${API.workOrder}/save`, payload)
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    addMechanic: action((state, payload) => {
        const exists = state.data.mechanics.some(m => m.mechanicId === payload.mechanicId);
        if (!exists) {
            state.data.mechanics.push(payload);
        }
    }),
    removeMechanic: action((state, mechanicRowId) => {
        state.data.mechanics = state.data.mechanics.filter(m => m.id !== mechanicRowId);
    }),
    updateWorkOrderMechanic: thunk((actions, payload) => {
        return axios.post<null, WorkOrderMechanicResponse>(`${API.workOrder}/mechanic`, payload)
            .then(response => {
                if (response.success) {
                    if (payload.operation === 'add' && response.data) {
                        actions.addMechanic(response.data);
                    } else if (payload.operation === 'remove') {
                        actions.removeMechanic(payload.id);
                    }
                }
                return response;
            });
    }),
    updateWorkOrderDataByKey: action((state, { key, value }) => {
        applyWorkOrderDataByKey(state.data, key, value);
    }),
    autoSaveWorkOrderData: thunk((actions, payload) => {
        return axios.post<null, WorkOrderAutoSaveDataResponse>(`${API.workOrder}/auto-save-data`, payload)
            .then(response => {
                if (response.success) {
                    actions.updateWorkOrderDataByKey({ key: payload.key, value: payload.value as string });
                }
                return response;
            });
    }),
    saveAdministrativeData: thunk((actions, payload) => {
        return axios.post<null, SaveAdministrativeDataResponse>(`${API.workOrder}/save-administrative-data`, payload)
            .then(data => data)
    }),
    timeEntrySaving: false,
    setTimeEntrySaving: action((state, payload) => {
        state.timeEntrySaving = payload;
    }),
    saveWorkOrderTimeEntry: thunk((actions, payload) => {
        actions.setTimeEntrySaving(true);
        return axios.post<null, WorkOrderTimeEntryResponse>(`${API.workOrder}/timeentry`, payload)
            .then(data => data)
            .finally(() => actions.setTimeEntrySaving(false));
    }),
    updateWorkOrderTimeEntry: thunk((actions, payload) => {
        actions.setTimeEntrySaving(true);
        return axios.patch<null, WorkOrderTimeEntryResponse>(`${API.workOrder}/timeentry`, payload)
            .then(data => data)
            .finally(() => actions.setTimeEntrySaving(false));
    }),
    deleteWorkOrderTimeEntry: thunk((actions, id) => {
        actions.setTimeEntrySaving(true);
        return axios.delete<null, WorkOrderTimeEntryResponse>(`${API.workOrder}/timeentry/${id}`)
            .then(data => data)
            .finally(() => actions.setTimeEntrySaving(false));
    }),
    attachmentSaving: false,
    setAttachmentSaving: action((state, payload) => {
        state.attachmentSaving = payload;
    }),
    saveIssueServiceAttachment: thunk((actions, payload) => {
        actions.setAttachmentSaving(true);
        const formData = jsonToFormData({
            id: payload.id,
            type: payload.type,
            files: payload.files,
        });
        return axios.post<null, WorkOrderAttachmentResponse>(`${API.workOrder}/issue-service/attachments/save`, formData)
            .then(response => response)
            .finally(() => actions.setAttachmentSaving(false));
    }),
    deleteIssueServiceAttachment: thunk((actions, payload) => {
        actions.setAttachmentSaving(true);
        return axios.post<null, WorkOrderAttachmentResponse>(`${API.workOrder}/issue-service/attachments/delete`, payload)
            .then(response => response)
            .finally(() => actions.setAttachmentSaving(false));
    }),
    saveWorkOrderAttachment: thunk((actions, payload) => {
        actions.setAttachmentSaving(true);
        const formData = jsonToFormData({
            workOrderId: payload.workOrderId,
            files: payload.files,
        });
        return axios.post<null, WorkOrderAttachmentResponse>(`${API.workOrder}/attachments`, formData)
            .then(response => response)
            .finally(() => actions.setAttachmentSaving(false));
    }),
    deleteWorkOrderAttachment: thunk((actions, payload) => {
        actions.setAttachmentSaving(true);
        return axios.delete<null, WorkOrderAttachmentResponse>(`${API.workOrder}/attachments/${payload.id}`)
            .then(response => response)
            .finally(() => actions.setAttachmentSaving(false));
    }),

    partUpdating: false,
    setPartUpdating: action((state, payload) => {
        state.partUpdating = payload;
    }),
    tempParts: [],
    setTempParts: action((state, payload) => {
        state.tempParts = payload;
    }),
    setParts: action((state, payload) => {
        state.data.parts = payload;
        state.tempParts = payload;
    }),
    fetchParts: thunk((actions, payload) => {
        return axios.post<null, WorkOrderPartList[]>(`${API.workOrderParts}/fetch/${payload}`)
            .then(
                data => {
                    actions.setParts(data);
                })
    }),
    searchParts: [],
    searchPartsTotal: 0,
    setSearchedParts: action((state, payload) => {
        state.searchParts = payload.parts;
        state.searchPartsTotal = payload.total;
    }),
    fetchSearchParts: thunk((actions, payload) => {
        actions.setPartUpdating(true);
        return axios.post<null, SearchPartResponse>(`${API.workOrderParts}/search`, payload).then(
            data => {
                actions.setSearchedParts(data);
            }
        ).finally(() => {
            actions.setPartUpdating(false);
        })
    }),
    savePart: thunk((actions, payload) => {
        actions.setPartUpdating(true);
        return axios.post<null, SavePartResponse>(`${API.workOrderParts}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setPartUpdating(false);
        })
    }),

    materialUpdating: false,
    setMaterialUpdating: action((state, payload) => {
        state.materialUpdating = payload;
    }),
    tempMaterials: [],
    setTempMaterials: action((state, payload) => {
        state.tempMaterials = payload;
    }),
    setMaterials: action((state, payload) => {
        state.data.materials = payload;
        state.tempMaterials = payload;
    }),
    fetchMaterials: thunk((actions, payload) => {
        return axios.get<null, Material[]>(`${API.workOrderMaterials}/fetch/${payload}`)
            .then(data => {
                actions.setMaterials(data);
            });
    }),
    miscMaterials: [],
    setMiscMaterials: action((state, payload) => {
        state.miscMaterials = payload;
    }),
    fetchMiscMaterials: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.get<null, MiscMaterial[]>(`${API.workOrderMaterials}/misc/${payload}`)
            .then(data => {
                actions.setMiscMaterials((data ?? []).filter(m => Number(m.id) !== 0));
            })
            .finally(() => {
                actions.setMaterialUpdating(false);
            });
    }),
    saveMaterials: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SaveMaterialResponse>(`${API.workOrderMaterials}/save`, payload).then(
            data => data
        ).finally(() => {
            actions.setMaterialUpdating(false);
        });
    })
};
